import { z } from 'zod';

/** A numeric-or-string scalar, as Tahiti returns ponderation weights (`"12"`). */
const numish = z.union([z.string(), z.number()]).nullish();

/**
 * A `pms_word` item in FLAT form — as returned by `GET /api/meta/list`, and as
 * produced by `api.ts`'s `unwrapObjectRow` after collapsing the `{value0,value}`
 * wrappers from `GET /api/meta/object`. Verified against devdb (2026-06): scalar
 * fields, `updated_at`/most columns often null, ponderation weights come back as
 * strings. Every field is optional-tolerant (the API omits columns not requested)
 * and unknown extra keys (`object_guid`, `"h2_table.code"`, …) are stripped.
 */
export const tahitiWordSchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  guid: z.string().optional(),
  title: z.string().nullish(),
  noyau_sing: z.string().nullish(),
  /** Curated/locked flag for `noyau_sing` — Tahiti returns booleans as strings ("true"/"false"). */
  noyau_validated: z.union([z.string(), z.boolean()]).nullish(),
  tags: z.string().nullish(),
  /** Markdown body (verified against devdb 2026-07: lives in `content`, not `description`). */
  content: z.string().nullish(),
  see_also: z.string().nullish(),
  // Six per-noyau integer weights (the noyau ponderation model).
  ponderation_sgp: numish,
  ponderation_spg: numish,
  ponderation_psg: numish,
  ponderation_pgs: numish,
  ponderation_gps: numish,
  ponderation_gsp: numish,
  // Read-only display fields (algorithm / Lexcore / Patrick owned).
  fonction: z.string().nullish(),
  genre: z.string().nullish(),
  /** Flagged-for-review flag — Tahiti returns booleans as strings ("true"/"false"), same as `noyau_validated`. */
  to_check: z.union([z.string(), z.boolean()]).nullish(),
  reasons: z.string().nullish(),
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
});
export type TahitiWord = z.infer<typeof tahitiWordSchema>;

/**
 * Every Tahiti response is wrapped in this envelope. `success` can be `false`
 * even on an HTTP 200, so the transport layer MUST check it explicitly.
 */
export function tahitiEnvelopeSchema<T>(dataSchema: z.ZodType<T>) {
  return z.object({
    success: z.boolean(),
    data: dataSchema,
    count: z.number().optional(),
    index: z.unknown().optional(),
  });
}

/** Error body shape returned when `success === false`. */
export const tahitiErrorSchema = z.object({
  success: z.literal(false),
  message: z.string().optional(),
  error: z
    .object({
      code: z.union([z.string(), z.number()]).optional(),
      details: z.unknown().optional(),
    })
    .optional(),
});
export type TahitiError = z.infer<typeof tahitiErrorSchema>;
